import axios from 'axios';
import { API_BASE_URL } from '../../utils/constants';
import { setLoginData } from '../userSlice';

/**
 * API MIDDLEWARE - Centralized API Request Handler
 * Pattern: Middleware-based pattern for managing all API calls
 *
 * Flow:
 * 1. Listen for 'api/callBegan' action with url, method, data, params
 * 2. Dispatch onStart reducer to set loading state
 * 3. Make axios request
 * 4. On success: dispatch onSuccess reducer with response data
 * 5. On failure: dispatch onError reducer with error details
 */

// Action type for API calls
export const API_CALL_BEGAN = 'api/callBegan';

// Action creator for API calls
export const apiCallBegan = (payload) => ({
    type: API_CALL_BEGAN,
    payload,
});

const apiCallHandler = async (url, method, data, params, onSuccess, meta, store, updateToken) => {
    // Make the API request with axios
    const config = {
        url,
        method,
        baseURL: API_BASE_URL,
        ...(data && { data }),
        ...(params && { params }),
    };

    if (updateToken) {
        config.headers = {
            'Authorization': updateToken,  // Capitalized header name
        };
        console.log('Auth token being sent:', updateToken.substring(0, 20) + '...');
    } else {
        // Add auth token if available from localStorage
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers = {
                'Authorization': token,  // Capitalized header name
            };
            console.log('Auth token being sent:', token.substring(0, 20) + '...');
        } else {
            console.warn('No auth token found in localStorage');
        }
    }

    const response = await axios(config);

    // Dispatch onSuccess action with response data and meta
    if (onSuccess) {
        // Handle array responses properly (don't spread arrays into objects)
        let payload;
        if (Array.isArray(response.data)) {
            // For array responses, wrap in data property if meta exists
            payload = meta ? { data: response.data, ...meta } : response.data;
        } else {
            // For object responses, merge with meta
            payload = { ...response.data, ...meta };
        }
        store.dispatch({
            type: onSuccess,
            payload,
        });
    }

    return response.data;
}

// Main middleware function
export const apiMiddleware = (store) => (next) => async (action) => {
    // Only handle apiCallBegan actions
    if (action.type !== API_CALL_BEGAN) {
        return next(action);
    }

    const {
        url,
        method = 'GET',
        data = null,
        params = null,
        onStart,
        onSuccess,
        onError,
        meta = null, // Meta data to pass to reducers (e.g., email for OTP)
        // showGlobalError = true, // Available for future use
    } = action.payload;

    // Dispatch onStart action to set loading state
    if (onStart) {
        store.dispatch({ type: onStart });
    }

    next(action);

    try {
        const response = await apiCallHandler(url, method, data, params, onSuccess, meta, store);
        return response;
    } catch (error) {
        if (error.response?.status === 401) {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
                try {
                    // Make the API request with axios
                    const config = {
                        url: "/refresh-token",
                        method: "POST",
                        baseURL: API_BASE_URL,
                        headers: {
                            'Authorization': refreshToken,  // Capitalized header name
                        }
                    };
                    console.log('Refresh token being sent:', refreshToken.substring(0, 20) + '...');
                    const response = await axios(config);
                    store.dispatch(setLoginData(response.data));
                    const recallResponse = await apiCallHandler(url, method, data, params, onSuccess, meta, store, response.data.token);
                    return recallResponse;
                } catch (error) {
                    console.error('Refresh token error:', error);
                    return null;
                }
            } else {
                console.error('No refresh token found in localStorage');
                return null;
            }
        }
        // Construct error object with message and code
        const errorObject = {
            errorMessage: error.response?.data?.message || error.response?.data?.detail || error.message || 'An error occurred',
            errorCode: error.response?.status || 500,
            errorDescription: error.response?.data?.description || error.response?.statusText || 'Unknown error',
        };

        // Dispatch onError action with error details
        if (onError) {
            store.dispatch({
                type: onError,
                payload: errorObject,
            });
        }

        console.error('API Error:', errorObject);
        return null;
    }
};
